import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { isEmpty, isNil, cloneDeep } from 'lodash-es';
import {
  type SitePrivateConfig,
  type MealPlan,
  type SitePublicConfig,
  type Tender,
  type ConfigTender,
  type ComposedTransactStationConfig,
  MealplanTypes,
} from '@centric-os/types';
import { DateTime } from 'luxon';
import { useFormStore } from '@centric-os/stores/form/use-form-store';
import ID from '@compassdigital/id';
import { useSiteConfigStore } from '../../../../../../routes';
import {
  ATRIUM_TYPES,
  CBORD_TYPES,
  DECLINING_BALANCE_WITH_SPLIT,
  FinancialFormIDs,
  ITC_TYPES,
  MEALPLAN_TYPES,
  TENDER_TYPES,
  TRANSACT_TYPES,
} from '../../../enums';
import { mapTenderConfig } from '@/views/app-modules/sites/src/routes/app-config/composables/use-app-config/map-mealplan-config/tender';
import { mapTenderPayload } from '@/views/app-modules/sites/src/routes/app-config/composables/use-app-config-mapper/map-mealplan-config-payload/tender';

export default () => {
  // Set site configurations store
  const siteConfigStore = useSiteConfigStore();
  const { sitePrivateConfig, sitePublicConfig } = storeToRefs(siteConfigStore);

  // Set localization

  // Helper function to get terminal IDs from string
  const getTerminalIds = (terminalIdString: string | undefined): string[] | undefined => {
    return terminalIdString?.split(',') ?? [];
  };

  // Computed for meal plan initial values
  const initialValues = computed<Partial<MealPlan>>(() => {
    const siteConfig = sitePrivateConfig.value?.mealplan?.[0];
    const publicConfig = sitePublicConfig.value?.mealplan?.[0];

    if (isNil(siteConfig) && isNil(publicConfig)) {
      return {}; // No meal plan found
    }

    // CBORD meal plan is not supported in private site config
    const mealPlan: MealPlan = isNil(siteConfig)
      ? (cloneDeep(publicConfig) as MealPlan)
      : cloneDeep(siteConfig);

    // If type is undefined or null, fallback to public config's type
    if (isNil(mealPlan.type)) {
      mealPlan.type = cloneDeep(publicConfig?.type);
    }

    let updatePlan: Partial<MealPlan> = {};

    // Common fields for all meal plans
    const basePlan: Partial<MealPlan> = {
      id: mealPlan.id,
      name: mealPlan.name,
      type: mealPlan.type,
    };

    // Mapping logic based on meal plan type
    if (ITC_TYPES.includes(mealPlan.type)) {
      updatePlan = {
        ...basePlan,
        keystore: {
          certificate: cloneDeep(mealPlan.keystore?.certificate),
          device_id: mealPlan.keystore?.device_id,
          endpoint: mealPlan.keystore?.endpoint,
          vendor_id: mealPlan.keystore?.vendor_id,
          webview: mealPlan.keystore?.webview,
        },
        tenders: publicConfig?.tenders?.map(mapTenderConfig) ?? ([] as ConfigTender[]),
      };
    } else if (ATRIUM_TYPES.includes(mealPlan.type)) {
      updatePlan = {
        ...basePlan,
        keystore: {
          key: mealPlan.keystore?.key,
          API_URL: mealPlan.keystore?.API_URL,
        },
        config: {
          // @ts-expect-error FIXME: Type 'string[]' is not assignable to type 'string'.
          terminal_id: getTerminalIds(mealPlan.config?.terminal_id as string),
        },
        note: cloneDeep(publicConfig?.note),
        tenders: publicConfig?.tenders?.map(mapTenderConfig) ?? ([] as ConfigTender[]),
        valid_email_domains: publicConfig?.valid_email_domains,
      };

      if (mealPlan.type === MEALPLAN_TYPES.ATRIUM_SSO) {
        updatePlan.keystore.client = mealPlan.keystore?.client;
        updatePlan.keystore.webview = mealPlan.keystore?.webview;
      }
    } else if (mealPlan.type === MEALPLAN_TYPES.HEARTLAND) {
      updatePlan = {
        ...basePlan,
        note: cloneDeep(publicConfig?.note),
        tenders: publicConfig?.tenders?.map(mapTenderConfig) ?? ([] as ConfigTender[]),
        keystore: {
          operator_id: mealPlan.keystore?.operator_id,
          operator_password: mealPlan.keystore?.operator_password,
          api_url: mealPlan.keystore?.api_url,
        },
        terminal_id: getTerminalIds(publicConfig?.terminal_id as string),
        tax_exempt: publicConfig?.tenders?.[0]?.tax_exempt,
      };
    } else if (TRANSACT_TYPES.includes(mealPlan.type)) {
      const hasInstitutionId = Boolean((mealPlan.keystore as any)?.institutionId);
      updatePlan = {
        ...basePlan,
        note: cloneDeep(publicConfig?.note),
        keystore: {
          host: mealPlan.keystore?.host,
          aes_key: mealPlan.keystore?.aes_key,
          port: mealPlan.keystore?.port,
          vendor_number: mealPlan.keystore?.vendor_number,
          use_sso: hasInstitutionId,
        },
        terminal_id: getTerminalIds(publicConfig?.terminal_id as string),
        tenders: publicConfig?.tenders?.map(mapTenderConfig) ?? ([] as ConfigTender[]),
        valid_email_domains: publicConfig?.valid_email_domains,
        transactSSO: {
          institutionId: (mealPlan.keystore as any)?.institutionId,
        },
      } as any;
    } else if (CBORD_TYPES.includes(mealPlan.type)) {
      // CBORD keystore data is stored on keystore obj in private site config
      const cbordKeystore = sitePrivateConfig.value?.keystore?.mealplan;

      updatePlan = {
        ...basePlan,
        note: cloneDeep(publicConfig?.note),
        keystore: {
          uuid: cbordKeystore?.uuid,
        },
        terminal_id: publicConfig?.terminal_id,
        tenders: publicConfig?.tenders?.map(mapTenderConfig) ?? [],
      };

      if (mealPlan.type === MEALPLAN_TYPES.CBORD) {
        updatePlan.keystore.auth_url = cbordKeystore?.auth_url;
      }
    }

    return updatePlan;
  });

  // Computed for meal plan availability (is based on private site config except for CBORD)
  const hasMealPlan = computed<boolean>(() => {
    return (
      (!isNil(sitePrivateConfig.value?.mealplan) && !isEmpty(sitePrivateConfig.value?.mealplan)) ||
      !isEmpty(sitePublicConfig.value?.mealplan)
    );
  });

  // Computed for meal plan types
  const mealPlanTypes = computed<{ label: string; value: string }[]>(() => {
    const { t } = useI18n();
    const types = [
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.transactPremise'),
        value: MEALPLAN_TYPES.BLACKBOARD,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.transactSaaS'),
        value: MEALPLAN_TYPES.BLACKBOARD_SAAS,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.cbordWeb'),
        value: MEALPLAN_TYPES.CBORD,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.itcStandard'),
        value: MEALPLAN_TYPES.ITCSTANDARD,
      },
      {
        label: t(
          'sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.itcDietaryRestriction',
        ),
        value: MEALPLAN_TYPES.ITCDIETARYRESTRICTION,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.itcLunchBox'),
        value: MEALPLAN_TYPES.ITCLUNCHBOX,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.itcSSO'),
        value: MEALPLAN_TYPES.ITCSSO,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.touchNET'),
        value: MEALPLAN_TYPES.HEARTLAND,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.atrium'),
        value: MEALPLAN_TYPES.ATRIUM,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.atriumSSO'),
        value: MEALPLAN_TYPES.ATRIUM_SSO,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.mealPlanTypes.cbordDirect'),
        value: MEALPLAN_TYPES.CBORD_DIRECT,
      },
    ];

    // Sort types by label alphabetically
    return types.toSorted((a, b) => a.label.localeCompare(b.label));
  });

  // Compute for meal plan tenders
  const tenderTypes = computed<{ label: string; value: string }[]>(() => {
    const { t } = useI18n();
    const types = [
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.tenderTypes.mealEquivalent'),
        value: TENDER_TYPES.MEAL_EQUIVALENT,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.tenderTypes.decliningBalance'),
        value: TENDER_TYPES.DECLINING_BALANCE,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.tenderTypes.stipend'),
        value: DECLINING_BALANCE_WITH_SPLIT,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.tenderTypes.mealExchange'),
        value: TENDER_TYPES.MEAL_EXCHANGE,
      },
    ];

    // Sort tenders by label alphabetically
    return types.toSorted((a, b) => a.label.localeCompare(b.label));
  });

  // Computed for tax exempt binary options
  const taxExemptOptions = computed<{ label: string; value: boolean }[]>(() => {
    const { t } = useI18n();
    const options = [
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.tenders.exemptYes'),
        value: true,
      },
      {
        label: t('sites.singleSite.financialConfig.mealPlans.form.tenders.exemptNo'),
        value: false,
      },
    ];
    return options;
  });

  // Computed for meal plan count required fields (not ideal but it will do for now)
  const requiredFields = computed<number>(() => {
    // Set form store
    const formStore = useFormStore();
    const form = formStore.getForm(FinancialFormIDs.MEAL_PLAN_FORM);

    if (isNil(form) || isNil(form.value)) {
      return 0;
    }

    // Required fields for cbord direct
    // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
    if (form.value?.values.type === MEALPLAN_TYPES.CBORD_DIRECT) {
      return 3;
      // cbord web
      // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
    } else if (form.value?.values.type === MEALPLAN_TYPES.CBORD) {
      return 4;
      // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
    } else if (form.value?.values.type === MEALPLAN_TYPES.HEARTLAND) {
      return 8;
      // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
    } else if (ITC_TYPES.includes(form.value?.values.type)) {
      return 9;
      // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
    } else if (ATRIUM_TYPES.includes(form.value?.values.type)) {
      // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
      if (form.value?.values.type === MEALPLAN_TYPES.ATRIUM_SSO) {
        return 6;
      } else {
        return 4;
      }
      // @ts-expect-error FIXME: Property 'type' does not exist on type 'unknown'.
    } else if (TRANSACT_TYPES.includes(form.value?.values.type)) {
      return 7;
    }

    return 0;
  });

  // Map Site Private Config payload
  const mapSitePrivateConfigPayload = (values: Partial<MealPlan>): SitePrivateConfig => {
    // Prepare payload
    // Site config
    const siteConfigStore = useSiteConfigStore();
    const { sitePrivateConfig } = storeToRefs(siteConfigStore);

    const payload: SitePrivateConfig = {
      ...sitePrivateConfig.value,
      keystore: {
        ...sitePrivateConfig.value?.keystore,
        mealplan: {},
      },
    };

    if (values.type === MEALPLAN_TYPES.CBORD_DIRECT) {
      payload.keystore.mealplan = {
        uuid: values.keystore?.uuid,
        type: values.type,
        name: values.name,
        auth_type: 1,
      };
    } else if (values.type === MEALPLAN_TYPES.CBORD) {
      payload.keystore.mealplan = {
        uuid: values.keystore?.uuid,
        auth_url: values.keystore?.auth_url,
        type: values.type.toLowerCase(),
        name: values.name,
        auth_type: 2,
      };
    } else if (values.type === MEALPLAN_TYPES.HEARTLAND) {
      const mealPlan: Partial<MealPlan> = {
        id: values.id,
        keystore: {
          api_url: values.keystore?.api_url,
          operator_id: values.keystore?.operator_id,
          operator_password: values.keystore?.operator_password,
        },
        name: values.name,
        type: values.type,
      };

      payload.mealplan = [mealPlan as MealPlan];
    } else if (ITC_TYPES.includes(values.type)) {
      const mealPlan: Partial<MealPlan> = {
        id: values.id,
        keystore: {
          certificate: values.keystore?.certificate,
          // @ts-expect-error FIXME: Type 'string' is not assignable to type 'number'.
          device_id: String(values.keystore?.device_id),
          endpoint: values.keystore?.endpoint,
          vendor_id: values.keystore?.vendor_id,
          webview: values.keystore?.webview,
        },
        name: values.name,
        type: values.type,
      };

      payload.mealplan = [mealPlan as MealPlan];
    } else if (TRANSACT_TYPES.includes(values.type)) {
      const transactValues = values as Partial<ComposedTransactStationConfig>;
      const aesKey = transactValues.keystore?.aes_key;
      const keystore: Record<string, any> = {
        // config types aes_key as a string and rejects the site config on read if it is stored as a number
        aes_key: aesKey == null ? aesKey : String(aesKey),
        host: transactValues.keystore?.host,
        manual_id_entry: 'T',
        port: transactValues.keystore?.port,
        vendor_number: transactValues.keystore?.vendor_number,
      };
      // only add the institution id if it exists
      if (transactValues.transactSSO?.institutionId) {
        keystore.institutionId = transactValues.transactSSO.institutionId;
      }

      const mealPlan: Partial<MealPlan> = {
        id: values.id,
        keystore,
        name: values.name,
      };

      payload.mealplan = [mealPlan as MealPlan];
    } else if (ATRIUM_TYPES.includes(values.type)) {
      const mealPlan: Partial<MealPlan> = {
        id: values.id,
        config: {
          // @ts-expect-error FIXME: Conversion of type 'string' to type 'string[]' may be a mistake because neith...
          terminal_id: (values.config?.terminal_id as string[])?.join(','),
          version: '1',
          tenders: values.tenders?.map((tender) =>
            mapTenderPayload(tender as Partial<Tender>, values.type),
          ),
        },
        keystore: {
          API_URL: values.keystore?.API_URL,
          key: values.keystore?.key,
        },
        name: values.name,
        type: values.type,
        version: 1,
      };

      if (values.type === MEALPLAN_TYPES.ATRIUM_SSO) {
        mealPlan.keystore.webview = values.keystore?.webview;
        mealPlan.keystore.client = values.keystore?.client;
      }

      payload.mealplan = [mealPlan as MealPlan];
    }

    return cloneDeep(payload);
  };

  // Map Site Public Config payload
  const mapSitePublicConfigPayload = (values: Partial<MealPlan>): SitePublicConfig => {
    // Prepare payload
    const payload: SitePublicConfig = {
      ...sitePublicConfig.value,
      mealplan: [
        {
          name: values.name,
          type: values.type,
          tenders: values.tenders?.map((tender) =>
            mapTenderPayload(tender as Partial<Tender>, values.type as MealplanTypes),
          ),
        },
      ],
    };

    // CBORD public config
    if (CBORD_TYPES.includes(values.type)) {
      payload.mealplan[0].note = {
        en: values.note?.en,
      };

      payload.mealplan[0].id = values.id;
      payload.mealplan[0].terminal_id = values.terminal_id;
    }

    // TouchNet (heartland) public config
    if (values.type === MEALPLAN_TYPES.HEARTLAND) {
      payload.mealplan[0].note = {
        en: values.note?.en,
      };

      // Create terminal id string from array
      payload.mealplan[0].terminal_id = (values?.terminal_id as string[])?.join(',');

      payload.mealplan[0].id = values.id;

      payload.mealplan[0].tenders = [
        {
          id: values.tenders?.[0]?.id,
          tender_number: values.tenders?.[0]?.tender_number,
          // @ts-expect-error FIXME: Type 'string' is not assignable to type 'TenderTypes'.
          type: TENDER_TYPES.DECLINING_BALANCE,
          tax_exempt: values.tax_exempt,
          name: values.name,
        },
      ];
    }

    // ITC public config
    if (ITC_TYPES.includes(values.type)) {
      payload.mealplan[0].id = values.id;
      payload.mealplan[0].name = values.name;
      payload.mealplan[0].type = values.type;
    }

    // Transact public config
    if (TRANSACT_TYPES.includes(values.type)) {
      payload.mealplan[0].id = values.id;
      payload.mealplan[0].name = values.name;
      payload.mealplan[0].type = values.type;
      payload.mealplan[0].note = values.note;
      payload.mealplan[0].valid_email_domains = values.valid_email_domains;

      payload.mealplan[0].terminal_id = (values?.terminal_id as string[])?.join(',');
    }

    // Atrium public config
    if (ATRIUM_TYPES.includes(values.type)) {
      payload.mealplan[0].id = values.id;
      payload.mealplan[0].name = values.name;
      payload.mealplan[0].type = values.type;
      payload.mealplan[0].note = values.note;
      payload.mealplan[0].valid_email_domains = values.valid_email_domains;
    }

    return cloneDeep(payload);
  };

  // Map delete meal plan private config payload
  const mapDeletePrivateConfigPayload = (): SitePrivateConfig => {
    const payload: SitePrivateConfig = cloneDeep(sitePrivateConfig.value);
    delete payload.keystore.mealplan;
    payload.mealplan = [];
    return payload;
  };

  // Map delete meal plan public config payload
  const mapDeletePublicConfigPayload = (): SitePublicConfig => {
    const payload: SitePublicConfig = cloneDeep(sitePublicConfig.value);
    payload.mealplan = [];
    return payload;
  };

  return {
    initialValues,
    hasMealPlan,
    mealPlanTypes,
    tenderTypes,
    taxExemptOptions,
    requiredFields,
    mapSitePrivateConfigPayload,
    mapSitePublicConfigPayload,
    mapDeletePrivateConfigPayload,
    mapDeletePublicConfigPayload,
  };
};
